// Projects and their working plans (PRD §2.2, §3.10, PE1–PE3, PE7–PE8, PE10–PE11, PE14). A plan is
// one document: assignments with an hours-per-week array each, the payment schedule and non-labor
// costs. It loads and saves whole, which is what the grid editor does (§7.1).

import { addDays, addMonths, daysBetween, isIsoDate, mondayOf, monthOf, today, type Iso, type Month } from "./dates";
import { isInvoiceDay, type InvoiceDay } from "./billing-terms";
import { isIrregular, regularized, type RevenueType, type ScheduleTier } from "./revenue";
import { DEFAULT_PROBABILITY, isClosed, requiresReason, STATUSES, takesBaseline, type ProjectStatus } from "./status";

export const MAX_WEEKS = 260;
export const MAX_WEEKLY_HOURS = 80;

export const STATUS_LABEL: Readonly<Record<ProjectStatus, string>> = {
  prospect: "Prospect",
  opportunity: "Opportunity",
  pending: "Pending",
  booked: "Booked",
  delivered: "Delivered",
  lost: "Lost",
  cancelled: "Cancelled",
};

/** Pick lists for the reasons PRD §3.10 requires, so win/loss and cancellations can be reported. */
export const CLOSE_REASONS: Readonly<Record<"lost" | "cancelled", readonly string[]>> = {
  lost: ["Price", "Timing", "Chose a competitor", "No decision or budget", "Scope changed", "Other"],
  cancelled: ["Client budget cut", "Client priorities changed", "Scope finished early", "Delivery concerns", "Other"],
};

export const REVENUE_TYPES: readonly RevenueType[] = ["client-fee", "partner-funding", "referral", "reimbursable"];
export const REVENUE_TYPE_LABEL: Readonly<Record<RevenueType, string>> = {
  "client-fee": "Client fee",
  "partner-funding": "Microsoft / partner funding",
  referral: "Referral revenue",
  reimbursable: "Reimbursable expenses billed",
};

export const EXPENSE_CATEGORIES = ["subcontractor", "travel", "event", "software", "referral-fee", "other"] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];
export const EXPENSE_LABEL: Readonly<Record<ExpenseCategory, string>> = {
  subcontractor: "Subcontractor",
  travel: "Travel",
  event: "Event",
  software: "Software",
  "referral-fee": "Partner referral fee",
  other: "Other",
};

export interface Assignment {
  id: string;
  roleId: string;
  /** null for a placeholder, which costs at the role's standard rate (PE3). */
  personId: string | null;
  note?: string;
  /** Hours per week, one entry per plan week from the plan's start Monday. */
  hours: number[];
}

export interface ScheduleLine extends ScheduleTier {
  id: string;
  label?: string;
  /** This line's own invoice day; otherwise the client's invoice table, then the contract date (CF2). */
  invoiceDay?: InvoiceDay;
}

/** A non-labor cost (PE8): `amount` in each of `count` months from `firstMonth`. */
export interface PlanExpense {
  id: string;
  category: ExpenseCategory;
  label: string;
  amount: number;
  firstMonth: Month;
  count: number;
  /** Billed back to the client: equal revenue and cost, so it never moves margin (§3.6). */
  reimbursable: boolean;
}

export interface WorkingPlan {
  startMonday: Iso;
  weeks: number;
  assignments: Assignment[];
  schedule: ScheduleLine[];
  expenses: PlanExpense[];
  /** After a resnap (PP3): weeks before this Monday are reported hours, frozen. */
  frozenBefore?: Iso;
}

export interface StatusChange {
  status: ProjectStatus;
  at: string;
  by: string;
  /** The effective date: the day work stopped for a cancellation, the decision date for a loss. */
  date?: Iso;
  reason?: string;
  note?: string;
}

/** One entry per owner a project has had, oldest first; the last is the current owner. */
export interface OwnerChange {
  /** The person on the People list; absent for the creator recorded before owners were picked. */
  personId?: string;
  name: string;
  at: string;
  by: string;
  note?: string;
}

export interface Project {
  id: string;
  code: string;
  name: string;
  clientName: string;
  status: ProjectStatus;
  /** Overrides the status default (§3.10). */
  probability?: number;
  /** Set for delivered, lost and cancelled. A cancelled project's weeks from this day are released. */
  closedOn?: Iso;
  billable: boolean;
  /** Templates are excluded from capacity, pipeline and financials (PE11). */
  isTemplate: boolean;
  offering?: string;
  /** The creator's sign-in, for projects whose owner was never picked from the People list. */
  ownerOid?: string;
  ownerName?: string;
  /** The owner picked from the People list; an own-projects caller matches them by email (SEC2). */
  ownerPersonId?: string;
  ownerEmail?: string;
  ownerHistory?: OwnerChange[];
  notes?: string;
  /** Overrides the company target margin for this project. */
  targetMargin?: number;
  /** The project this one would renew: a potential renewal (PF3, rev. 1.20). */
  renewalOf?: string;
  /** The contract's date: invoices go out on its day of the month unless the client's table says otherwise (CF2). */
  contractDate?: Iso;
  /** The first and last days the contract covers: hours can go to the project only between them (Chris, 2026-10-28). */
  contractStart?: Iso;
  contractEnd?: Iso;
  statusHistory: StatusChange[];
  /** How many baselines exist; Baseline 1 is taken automatically at booking (PE14). */
  baselines: number;
  plan: WorkingPlan;
  createdAt: string;
  createdBy: string;
}

export const probabilityOf = (p: Pick<Project, "status" | "probability">): number => p.probability ?? DEFAULT_PROBABILITY[p.status];

/** The Sunday after the plan's last week. */
export const planEnd = (plan: Pick<WorkingPlan, "startMonday" | "weeks">): Iso => addDays(plan.startMonday, plan.weeks * 7 - 1);

/** The days a project's contract covers; without contract dates, the plan's first and last weeks stand in. */
export const contractWindow = (p: Pick<Project, "contractStart" | "contractEnd" | "plan">): { from: Iso; to: Iso } => ({
  from: p.contractStart ?? p.plan.startMonday,
  to: p.contractEnd ?? planEnd(p.plan),
});

/**
 * Can hours on `day` go to this project (Chris, 2026-10-28)? Only inside its contract, for compliance:
 * nobody, an admin included, codes time to a project before its contract starts or after it closes.
 * If the work really happened, the contract dates move, or the hours go to another code. Expenses
 * aren't bound by this. The contract dates are what decide the days (Chris, 2026-11-03); the status
 * decides only whether the project takes hours at all: booked and delivered do, a cancelled one until
 * work stops, anything else never. When it was marked booked doesn't matter.
 */
export const chargeableOn = (p: Pick<Project, "status" | "closedOn" | "contractStart" | "contractEnd" | "plan">, day: Iso): boolean => {
  const w = contractWindow(p);
  if (day < w.from || day > w.to) return false;
  if (p.status === "cancelled") return !!p.closedOn && day < p.closedOn;
  return p.status === "booked" || p.status === "delivered";
};

export const newId = (): string => crypto.randomUUID();

export const emptyPlan = (startMonday: Iso, weeks: number): WorkingPlan => ({ startMonday, weeks, assignments: [], schedule: [], expenses: [] });

// --- Validation ----------------------------------------------------------------------------------

const isMonth = (v: unknown): v is Month => typeof v === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(v);
const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const int = (v: unknown, lo: number, hi: number) => Number.isInteger(v) && (v as number) >= lo && (v as number) <= hi;

/** Codes are what reported hours map by (PE12): letters and digits, like NT001 or 3S002. */
export const normalizeCode = (code: string) => code.trim().toUpperCase();
export const isValidCode = (code: unknown) => typeof code === "string" && /^[A-Z0-9]{2,12}$/.test(code);

export function validateDetails(p: Partial<Project>): string[] {
  const errors: string[] = [];
  if (!isValidCode(p.code)) errors.push("A project code is 2 to 12 letters and digits, like NT001.");
  if (typeof p.name !== "string" || !p.name.trim() || p.name.length > 120) errors.push("A project needs a name of up to 120 characters.");
  if (typeof p.clientName !== "string" || p.clientName.length > 120) errors.push("The client name is too long.");
  if (!STATUSES.includes(p.status as ProjectStatus)) errors.push("Pick a status.");
  if (p.probability !== undefined && (!num(p.probability) || p.probability < 0 || p.probability > 1)) errors.push("Probability must be between 0% and 100%.");
  if (p.targetMargin !== undefined && (!num(p.targetMargin) || p.targetMargin < 0 || p.targetMargin >= 1)) errors.push("A target margin must be between 0% and 100%.");
  if (typeof p.billable !== "boolean" || typeof p.isTemplate !== "boolean") errors.push("Say whether the project is billable and whether it is a template.");
  if (p.notes !== undefined && (typeof p.notes !== "string" || p.notes.length > 2_000)) errors.push("Notes are limited to 2,000 characters.");
  if (p.renewalOf !== undefined && (typeof p.renewalOf !== "string" || !p.renewalOf || p.renewalOf.length > 64)) errors.push("Pick the project this one renews.");
  if (p.renewalOf !== undefined && p.renewalOf === p.id) errors.push("A project can't renew itself.");
  if (p.contractDate !== undefined && !isIsoDate(p.contractDate)) errors.push("The contract date isn't a valid date.");
  if (p.contractStart !== undefined && !isIsoDate(p.contractStart)) errors.push("The contract start isn't a valid date.");
  if (p.contractEnd !== undefined && !isIsoDate(p.contractEnd)) errors.push("The contract end isn't a valid date.");
  if (p.contractStart && p.contractEnd && p.contractEnd < p.contractStart) errors.push("The contract can't end before it starts.");
  return errors;
}

export function validatePlan(plan: Partial<WorkingPlan>, roles: ReadonlySet<string>, people: ReadonlySet<string>): string[] {
  const errors: string[] = [];
  if (!isIsoDate(plan.startMonday) || mondayOf(plan.startMonday) !== plan.startMonday) errors.push("A plan starts on a Monday.");
  if (!int(plan.weeks, 1, MAX_WEEKS)) errors.push(`A plan runs 1 to ${MAX_WEEKS} weeks.`);
  if (!Array.isArray(plan.assignments) || !Array.isArray(plan.schedule) || !Array.isArray(plan.expenses)) return [...errors, "The plan is incomplete."];
  const ids = new Set<string>();
  const unique = (id: unknown, what: string) => {
    if (typeof id !== "string" || !id || ids.has(id)) errors.push(`Every ${what} needs its own id.`);
    else ids.add(id);
  };
  for (const a of plan.assignments) {
    unique(a?.id, "assignment");
    if (!roles.has(a?.roleId)) errors.push("Every assignment needs a role from the list.");
    if (a?.personId !== null && !people.has(a?.personId)) errors.push("An assignment names someone who isn't on the people list.");
    if (!Array.isArray(a?.hours) || a.hours.length !== plan.weeks) errors.push("An assignment's hours don't match the plan's weeks.");
    else if (a.hours.some((h) => !num(h) || h < 0 || h > MAX_WEEKLY_HOURS)) errors.push(`Weekly hours must be between 0 and ${MAX_WEEKLY_HOURS}.`);
  }
  for (const s of plan.schedule) {
    unique(s?.id, "payment line");
    if (!num(s?.amount) || s.amount < 0) errors.push("Every payment needs an amount of zero or more.");
    if (!["once", "monthly", "quarterly"].includes(s?.frequency)) errors.push("A payment is once, monthly or quarterly.");
    if (!int(s?.count, 1, 120)) errors.push("A payment repeats 1 to 120 times.");
    if (!isMonth(s?.firstInvoiceMonth)) errors.push("Every payment needs a first invoice month.");
    if (!int(s?.monthsCovered, 1, 36)) errors.push("A payment covers 1 to 36 months of service.");
    else if (isIrregular(s)) errors.push("A monthly payment pays for its month, and a quarterly one for its quarter.");
    if (s?.type !== undefined && !REVENUE_TYPES.includes(s.type)) errors.push("Pick a revenue type.");
    if (s?.invoiceDay !== undefined && !isInvoiceDay(s.invoiceDay)) errors.push("An invoice day is the contract date's day, the last day, or a day from 1 to 31.");
  }
  for (const e of plan.expenses) {
    unique(e?.id, "cost");
    if (!EXPENSE_CATEGORIES.includes(e?.category)) errors.push("Pick a cost category.");
    if (typeof e?.label !== "string" || !e.label.trim() || e.label.length > 80) errors.push("Give each cost a short label.");
    if (!num(e?.amount) || e.amount < 0) errors.push("Every cost needs an amount of zero or more.");
    if (!isMonth(e?.firstMonth)) errors.push("Every cost needs a month.");
    if (!int(e?.count, 1, 120)) errors.push("A cost repeats for 1 to 120 months.");
    if (typeof e?.reimbursable !== "boolean") errors.push("Say whether each cost is reimbursable.");
  }
  return [...new Set(errors)];
}

// --- Status lifecycle (§3.10) --------------------------------------------------------------------

export interface StatusRequest {
  status: ProjectStatus;
  date?: Iso;
  reason?: string;
  note?: string;
  probability?: number;
}

/**
 * Applies a status change: lost and cancelled need a reason and a date; any closed status sets
 * `closedOn`; reopening clears it. Returns whether Baseline 1 (or the next baseline) is due.
 */
export function changeStatus(project: Project, req: StatusRequest, by: string, at = new Date().toISOString()): { project: Project; errors: string[]; baselineDue: boolean } {
  const errors: string[] = [];
  if (!STATUSES.includes(req.status)) errors.push("Pick a status.");
  if (req.date !== undefined && !isIsoDate(req.date)) errors.push("The date isn't valid.");
  if (requiresReason(req.status)) {
    if (!req.reason?.trim()) errors.push(`Moving to ${STATUS_LABEL[req.status].toLowerCase()} needs a reason.`);
    if (!req.date) errors.push(`Moving to ${STATUS_LABEL[req.status].toLowerCase()} needs a date.`);
  }
  if (req.probability !== undefined && (!num(req.probability) || req.probability < 0 || req.probability > 1)) errors.push("Probability must be between 0% and 100%.");
  if (project.isTemplate && req.status !== project.status) errors.push("A template has no status of its own. Create a project from it instead.");
  if (errors.length) return { project, errors, baselineDue: false };

  const next: Project = { ...project, status: req.status, probability: req.probability };
  if (isClosed(req.status)) next.closedOn = req.date ?? today();
  else delete next.closedOn;
  next.statusHistory = [
    ...project.statusHistory,
    { status: req.status, at, by, date: req.date, reason: req.reason?.trim() || undefined, note: req.note?.trim() || undefined },
  ];
  return { project: next, errors, baselineDue: takesBaseline(project.status, req.status) };
}

// --- Copies and templates (PE10, PE11) -----------------------------------------------------------

/**
 * Resizes an hours-per-week row to a new number of weeks. "rate" keeps hours per week, so the
 * pattern stretches or compresses and total hours change. "total" keeps total hours, so hours per
 * week rescale. Each new week takes the value of the old week at the same relative position.
 */
export function stretchHours(hours: readonly number[], weeks: number, mode: "rate" | "total"): number[] {
  if (weeks === hours.length) return [...hours];
  const out = Array.from({ length: weeks }, (_, j) => hours[Math.min(hours.length - 1, Math.floor((j * hours.length) / weeks))] ?? 0);
  if (mode === "rate") return out;
  const before = hours.reduce((a, b) => a + b, 0);
  const after = out.reduce((a, b) => a + b, 0);
  if (!after) return out;
  return out.map((h) => Math.min(MAX_WEEKLY_HOURS, Math.round(((h * before) / after) * 100) / 100));
}

export interface CopyOptions {
  startMonday: Iso;
  /** Named people, or turn every assignment into a placeholder for its role. */
  keepPeople: boolean;
  keepHours: boolean;
  keepSchedule: boolean;
  keepExpenses: boolean;
  /** A new duration, for "New from template" (PE11). */
  weeks?: number;
  stretch?: "rate" | "total";
}

/**
 * A copy of a plan moved to a new start: every week, payment and cost shifts with it. Payments and
 * costs move by whole months, the gap between the old and new start months.
 */
export function copyPlan(source: WorkingPlan, o: CopyOptions): WorkingPlan {
  const weeks = o.weeks ?? source.weeks;
  const shift = monthsBetween(monthOf(source.startMonday), monthOf(o.startMonday));
  return {
    startMonday: o.startMonday,
    weeks,
    assignments: source.assignments.map((a) => ({
      id: newId(),
      roleId: a.roleId,
      personId: o.keepPeople ? a.personId : null,
      note: a.note,
      hours: o.keepHours ? stretchHours(a.hours, weeks, o.stretch ?? "rate") : new Array(weeks).fill(0),
    })),
    schedule: o.keepSchedule ? source.schedule.map((s) => ({ ...regularized(s), id: newId(), firstInvoiceMonth: addMonths(s.firstInvoiceMonth, shift) })) : [],
    expenses: o.keepExpenses ? source.expenses.map((e) => ({ ...e, id: newId(), firstMonth: addMonths(e.firstMonth, shift) })) : [],
  };
}

export function monthsBetween(from: Month, to: Month): number {
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  return (ty - fy) * 12 + (tm - fm);
}

/** Moves a plan's start by whole weeks, keeping each week's hours on the same calendar week where it can. */
export function resizePlan(plan: WorkingPlan, startMonday: Iso, weeks: number): WorkingPlan {
  const offset = Math.round(daysBetween(plan.startMonday, startMonday) / 7);
  return {
    ...plan,
    startMonday,
    weeks,
    assignments: plan.assignments.map((a) => ({ ...a, hours: Array.from({ length: weeks }, (_, w) => a.hours[w + offset] ?? 0) })),
  };
}

// --- Range commands (PE5) ------------------------------------------------------------------------

export type RangeOp =
  | { kind: "set"; value: number }
  | { kind: "scale"; percent: number }
  | { kind: "shift"; weeks: number }
  | { kind: "ramp"; from: number; to: number }
  | { kind: "clear" };

const clampHours = (h: number) => Math.min(MAX_WEEKLY_HOURS, Math.max(0, Math.round(h * 100) / 100));

/**
 * Applies a command to a block: the given assignment rows, weeks `first` to `last` inclusive.
 * Shift moves the block's hours by N weeks (negative moves earlier); anything pushed past the plan's
 * ends is dropped, and the weeks it leaves are cleared. Ramp runs linearly from `from` to `to`.
 */
export function applyRange(plan: WorkingPlan, rows: readonly number[], first: number, last: number, op: RangeOp): WorkingPlan {
  const lo = Math.max(0, Math.min(first, last));
  const hi = Math.min(plan.weeks - 1, Math.max(first, last));
  const span = hi - lo + 1;
  const assignments = plan.assignments.map((a, r) => {
    if (!rows.includes(r) || span <= 0) return a;
    const hours = [...a.hours];
    if (op.kind === "shift") {
      const block = hours.slice(lo, hi + 1);
      for (let w = lo; w <= hi; w++) hours[w] = 0;
      block.forEach((h, i) => {
        const to = lo + i + op.weeks;
        if (to >= 0 && to < plan.weeks) hours[to] = h;
      });
      return { ...a, hours };
    }
    for (let w = lo; w <= hi; w++) {
      const i = w - lo;
      hours[w] =
        op.kind === "set"
          ? clampHours(op.value)
          : op.kind === "scale"
            ? clampHours((hours[w] * op.percent) / 100)
            : op.kind === "ramp"
              ? clampHours(span === 1 ? op.from : op.from + ((op.to - op.from) * i) / (span - 1))
              : 0;
    }
    return { ...a, hours };
  });
  return { ...plan, assignments };
}
